import { useMemo, useState } from "react";

import { Popover, SearchField } from "@/components/kit";
import { emojiTag, searchSportEmoji, sportEmoji } from "@/lib/sportIcons";

import styles from "./IconPicker.module.css";

/**
 * The mark a sport wears.
 *
 * The catalogue and its search have existed in `lib/sportIcons` from the start
 * and nothing ever called them, so no sport could be given a mark and every one
 * of them wore the fallback trophy — including all six in the seed, which is
 * what hid the fact that the seed was writing ids from a different vocabulary
 * altogether.
 *
 * A picker rather than a text field, because the value is an id from a fixed
 * list: typing "cricket" and hoping is not a choice, it is a guess. Search is
 * over the *sports* each glyph stands for, not the glyph's own name — somebody
 * onboarding kabaddi types "kabaddi" and should land on something, and the
 * catalogue tags say which glyphs claim it.
 */
export function IconPicker({
  value,
  onChange,
  id,
}: {
  value: string | null | undefined;
  onChange: (id: string) => void;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const chosen = sportEmoji(value);
  const matches = useMemo(() => searchSportEmoji(query), [query]);

  // Nothing chosen yet reads differently from a deliberate trophy, and the
  // difference matters: one is unanswered, the other is answered "generic".
  const unset = !value;

  return (
    <Popover
      open={open}
      onClose={() => setOpen(false)}
      wide
      trigger={
        <button
          type="button"
          id={id}
          className={styles.trigger}
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
        >
          <span className={styles.glyph} aria-hidden>
            {chosen.glyph}
          </span>
          <span className={styles.triggerText}>
            {unset ? "Choose a mark" : emojiTag(chosen)}
          </span>
        </button>
      }
    >
      <div className={styles.panel}>
        <SearchField
          value={query}
          placeholder="Search by sport — cricket, kabaddi, luge"
          aria-label="Search marks"
          autoFocus
          onChange={(e) => setQuery(e.target.value)}
        />

        {matches.length === 0 ? (
          <p className={styles.none}>
            Nothing matches “{query}”. The generic marks at the end of the list — a
            trophy, a medal, a stopwatch — cover a sport the catalogue does not name.
          </p>
        ) : (
          <div className={styles.grid} role="listbox" aria-label="Marks">
            {matches.map((emoji) => {
              const on = emoji.id === value;
              return (
                <button
                  key={emoji.id}
                  type="button"
                  role="option"
                  aria-selected={on}
                  // The tag, not the id: at 20px a glyph is often ambiguous, and
                  // "horse racing" settles 🏇 against 🐎 immediately.
                  title={emojiTag(emoji)}
                  aria-label={emojiTag(emoji)}
                  className={on ? `${styles.option} ${styles.optionOn}` : styles.option}
                  onClick={() => {
                    onChange(emoji.id);
                    setOpen(false);
                    setQuery("");
                  }}
                >
                  <span aria-hidden>{emoji.glyph}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Popover>
  );
}
